import { PlainButton } from '@components/shared/Button/styles'
import styled from '@emotion/styled'

export const Wrapper = styled.div(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  marginBottom: 20,
  position: 'relative',
  [theme.breakpoint.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  '& a, button': {
    position: 'absolute',
    right: -3,
    top: 0,
    width: 100,
  },
}))

export const Label = styled('label', {
  shouldForwardProp: prop => prop !== 'required',
})<{ required?: boolean }>(({ theme, required }) => ({
  fontSize: theme.fontSizes.desktop.body,
  fontWeight: theme.fontWeights.medium,
  color: theme.colors.N500,
  lineHeight: 1.15,
  marginBottom: 8,
  display: 'block',
  marginTop: 16,
  [theme.breakpoint.down('md')]: {
    fontSize: theme.fontSizes.mobile.body,
  },
  '&:after': {
    content: required ? "' *'" : undefined,
    color: required ? theme.colors.R500 : undefined,
  },
}))

export const Mewozlabel = styled.p(({ theme }) => ({
  fontSize: theme.fontSizes.desktop.body,
  fontWeight: theme.fontWeights.medium,
  color: theme.colors.N800,
  lineHeight: 24 / 16,
  marginBottom: 4,
  [theme.breakpoint.down('md')]: {
    fontSize: theme.fontSizes.mobile.body,
  },
}))

export const Select = styled.select(({ theme }) => ({}))

export const IconWrapper = styled.div(({ theme }) => ({
  position: 'absolute',
  left: 0,
  height: 56,
  pointerEvents: 'none',
  borderRight: `2px solid ${theme.colors.N50}`,
  width: 64,
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  order: -1,
  fontSize: theme.fontSizes.desktop.body,
  [theme.breakpoint.down('md')]: {
    fontSize: theme.fontSizes.mobile.body,
    width: 56,
    height: 44,
  },
}))

export const PasswordWrapper = styled.div(({ theme }) => ({
  position: 'absolute',
  right: 0,
  height: '100%',
  paddingRight: 19,
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  '&:hover': {
    cursor: 'pointer',
  },
  [theme.breakpoint.down('md')]: {
    paddingRight: 15,
  },
}))

export const Input = styled('input', {
  shouldForwardProp: prop => !['isDisabled', 'hasError', 'hasIcon'].includes(String(prop)),
})<{ isDisabled: boolean; hasError: boolean; hasIcon: boolean }>(({ theme, hasIcon, isDisabled, hasError }) => ({
  border: '2px solid',
  width: '100%',
  borderRadius: 6,
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
  height: '100%',
  paddingLeft: hasIcon ? 80 : 16,
  boxSizing: 'border-box',
  background: theme.colors.N0,
  color: isDisabled ? theme.colors.N300 : theme.colors.N600,
  fontSize: theme.fontSizes.desktop.body,
  outline: 'none',
  [`&, & + ${IconWrapper}`]: {
    borderColor: hasError ? theme.colors.Y300 : theme.colors.N50,
    borderBottomColor: hasError ? theme.colors.Y300 : theme.colors.N50,
  },
  '&:not([disabled]):not([readonly]):hover:not(:focus)': {
    [`&, & + ${IconWrapper}`]: {
      borderColor: isDisabled ? theme.colors.N50 : theme.colors.N80,
      borderBottomColor: isDisabled ? theme.colors.N50 : theme.colors.N80,
    },
  },
  '&:focus': {
    [`&, & + ${IconWrapper}`]: {
      borderColor: isDisabled ? theme.colors.N50 : theme.colors.B300,
      borderBottomColor: isDisabled ? theme.colors.N50 : theme.colors.B300,
    },
  },
  '&[disabled]': {
    cursor: 'not-allowed',
    background: theme.colors.N30,
    border: 0,
  },
  // '&[readonly]': {
  //   background: theme.colors.N30,
  //   border: 0,
  // },
  '&::placeholder': {
    fontSize: theme.fontSizes.desktop.body,
    color: theme.colors.N300,
    [theme.breakpoint.down('md')]: {
      fontSize: theme.fontSizes.desktop.body,
    },
  },
  [theme.breakpoint.down('md')]: {
    width: '100%',
    fontSize: theme.fontSizes.desktop.body, // DO NOT USE MOBILE SIZE, THIS WILL CREATE AN UNWANTED ZOOM IN IOS
  },
  '&.password': {
    paddingRight: 50,
  },
  '&[type="date"]::-webkit-inner-spin-button': {
    display: 'none',
  },
  '&[type="date"]::-webkit-calendar-picker-indicator': {
    display: 'none',
  },
}))

export const ErrorSpan = styled.p(({ theme }) => ({
  maxWidth: '100%',
  flex: '0 0 100%',
  border: 'none',
  marginTop: 4,
  textAlign: 'left',
  color: theme.colors.Y300,
  boxSizing: 'border-box',
  marginBottom: '0 !important',
  fontSize: `${theme.fonts.pxToRem(16)} !important`,
  lineHeight: 24 / 16,
  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
  [theme.breakpoint.down('md')]: {
    maxWidth: '100%',
    flex: '0 0 100%',
    width: '100%',
  },
  '&.noMargin': {
    marginLeft: 0,
  },
}))

export const HelperText = styled.p(({ theme }) => ({
  marginTop: 10,
  marginBottom: 0,
  maxWidth: '100%',
  flex: '0 0 100%',
  marginLeft: 'auto',
  color: theme.colors.N500,
  fontSize: theme.fonts.pxToRem(12),
  fontWeight: theme.fontWeights.thin,
  lineHeight: 1.2,
  [theme.breakpoint.down('md')]: {
    maxWidth: '100%',
    flex: '0 0 100%',
  },
  '& a': {
    color: theme.colors.N500,
    opacity: 1,
    transition: 'all 150ms ease-out',
    '&:hover': {
      opacity: 0.8,
    },
  },
}))

export const InputWrapper = styled.div(({ theme }) => ({
  marginTop: 8,
  position: 'relative',
  '& svg': {
    width: 14,
    height: 14,
  },
}))

export const StyledButton = styled(PlainButton)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  [theme.breakpoint.down('xs')]: {
    position: 'static',
    marginTop: 10,
    width: '100%',
  },
}))

export const FieldWrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'isLoading',
})<{ isLoading?: boolean }>(({ theme, isLoading }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  height: 56,
  alignItems: 'center',
  '&::after': {
    content: isLoading ? "''" : undefined,
    position: 'absolute',
    top: '50%',
    marginTop: -12,
    right: 20,
    display: 'block',
    width: 24,
    height: 24,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '24px 24px',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid' class='lds-rolling'%3E%3Ccircle cx='50' cy='50' fill='none' stroke='%232684FF' stroke-width='10' r='32' stroke-dasharray='150 52' transform='rotate(279.944 50 50)'%3E%3CanimateTransform attributeName='transform' type='rotate' calcMode='linear' values='0 50 50;360 50 50' keyTimes='0;1' dur='1s' begin='0s' repeatCount='indefinite'%3E%3C/animateTransform%3E%3C/circle%3E%3C/svg%3E")`,
  },
  [theme.breakpoint.down('md')]: {
    height: 44,
  },
}))

export const OuterWrapper = styled.div(({ theme }) => ({
  width: '100%',
  // [`&:first-child > ${Label}`]: {
  //     marginTop: 0,
  // }
}))
